import React from "react";
import { X, Info, Paperclip } from "lucide-react";
import AvatarInitials from "../atoms/AvatarInitials";

const STAGE_META = {
  1: { title: "Numérisation & Dépôt" },
  2: { title: "Pré-compta" },
  3: { title: "Révision" },
};

// Libellés des champs de clôture par étape — mêmes champs que le mail de
// clôture (clotureEmailController.js), affichés ici en lecture seule pour ne
// pas avoir à rouvrir ce mail. Étape 2 : uniquement les champs du/des
// volet(s) réellement renseigné(s) (Traitement comptable et/ou RB), jamais
// tout affiché en bloc — voir estTraitementComptable/estRB.
function champsPourEtape(etape) {
  if (etape.niveau === 1) {
    return [
      ["Qualité d'organisation", etape.qualiteOrganisation],
      ["Temps de traitement approximatif", etape.tempsTraitement],
    ];
  }
  if (etape.niveau === 2) {
    return [
      ...(etape.estTraitementComptable
        ? [
            ["Nombre de factures d'achats traitées", etape.nbrFactureAchat],
            ["Nombre de factures de ventes traitées", etape.nbrFactureVente],
            ["Nombre de documents non traités", etape.nbrDocumentsNonTraites],
            ["Ebics", etape.tcEbics],
          ]
        : []),
      ...(etape.estRB
        ? [
            ["Numéro de compte", etape.rbNumeroCompte],
            ["Mois traités", etape.rbMoisTraites],
            ["Solde RB", etape.rbSoldeRB],
            ["Solde 512", etape.rbSolde512],
            ["Écart", etape.rbEcart],
            ["Ebics (RB)", etape.rbEbics],
          ]
        : []),
    ];
  }
  return [
    ["Résultat du contrôle", etape.resultatControle],
    ["Temps passé (h)", etape.tempsPasse],
  ];
}

// Type de clôture de l'étape 2 — "Traitement comptable" par défaut pour tous
// les dossiers clôturés avant l'introduction du volet RB (valeurs par défaut
// en base : estTraitementComptable=true, estRB=false).
function typeClotureNiveau2(etape) {
  if (etape.estTraitementComptable && etape.estRB) return "Traitement comptable + RB";
  if (etape.estRB) return "RB";
  if (etape.estTraitementComptable) return "Traitement comptable";
  return null;
}

function fmtDateTime(value) {
  return value
    ? new Date(value).toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";
}

function ClotureDetailsOverlay({ ticket, etape, onClose }) {
  const meta = STAGE_META[etape.niveau];
  const champs = champsPourEtape(etape).filter(
    ([, value]) => value !== undefined && value !== null && value !== ""
  );
  const typeCloture = etape.niveau === 2 ? typeClotureNiveau2(etape) : null;
  const images = [
    ["Image débit", etape.rbImageDebitUrl],
    ["Image crédit", etape.rbImageCreditUrl],
  ].filter(([, url]) => !!url);

  return (
    <div className="fixed inset-0 bg-black/45 z-[1000] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#5C1A30] flex items-center justify-center flex-shrink-0">
              <Info className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900 leading-tight">Détails de la clôture</h2>
              <p className="text-xs text-gray-400">
                {ticket.client?.nom} · Niveau {etape.niveau} — {meta.title}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors flex-shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="flex items-center gap-3 bg-[#fff6ee] rounded-xl px-4 py-3">
            <AvatarInitials
              id={etape.closedBy?.id}
              name={etape.closedBy?.name}
              lastName={etape.closedBy?.lastName}
              variant="solid"
              size="w-9 h-9"
              textSize="text-xs"
            />
            <div>
              <p className="text-xs font-semibold text-[#5C1A30] uppercase tracking-wide">Clôturé par</p>
              <p className="text-sm font-bold text-gray-900">
                {etape.closedBy ? `${etape.closedBy.name} ${etape.closedBy.lastName || ""}` : "—"}
              </p>
              <p className="text-xs text-gray-400">{fmtDateTime(etape.closedAt)}</p>
            </div>
          </div>

          {etape.delaiJours !== null && etape.delaiJours !== undefined && (
            <p className="text-xs text-gray-500">
              Traité en <strong>{etape.delaiJours} jour{etape.delaiJours > 1 ? "s" : ""}</strong>
              {etape.clotureATemps === false ? (
                <span className="text-red-500 font-semibold"> — clôturé après échéance</span>
              ) : etape.clotureATemps === true ? (
                <span className="text-emerald-600 font-semibold"> — dans les délais</span>
              ) : null}
            </p>
          )}

          {typeCloture && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500">Type de clôture</span>
              <span className="text-xs font-semibold bg-[#5C1A30]/10 text-[#5C1A30] px-2.5 py-1 rounded-full">
                {typeCloture}
              </span>
            </div>
          )}

          {champs.length > 0 && (
            <div className="border border-gray-100 rounded-2xl p-4 space-y-2">
              {champs.map(([label, value]) => (
                <div key={label} className="flex items-center justify-between gap-4 text-sm">
                  <span className="text-gray-500">{label}</span>
                  <span className="font-semibold text-gray-900">{value}</span>
                </div>
              ))}
            </div>
          )}

          {images.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Images RB</p>
              <div className="flex gap-3">
                {images.map(([label, url]) => (
                  <a key={label} href={url} target="_blank" rel="noreferrer" className="block">
                    <img
                      src={url}
                      alt={label}
                      title={label}
                      className="w-24 h-24 object-cover rounded-xl border border-gray-200 hover:opacity-80 transition-opacity"
                    />
                    <p className="text-[11px] text-gray-400 text-center mt-1">{label}</p>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Fichiers joints à la clôture (5 au plus), sinon l'ancien document unique */}
          {(() => {
            const fichiers = Array.isArray(etape.fichiers) && etape.fichiers.length
              ? etape.fichiers
              : etape.documentUrl
                ? [{ url: etape.documentUrl, nom: etape.documentNom }]
                : [];
            if (!fichiers.length) return null;
            return (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">
                  {fichiers.length > 1 ? `Fichiers joints (${fichiers.length})` : "Pièce jointe"}
                </p>
                <div className="flex flex-col gap-1.5">
                  {fichiers.map((x, i) => (
                    <a
                      key={`${x.url}-${i}`}
                      href={x.url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 bg-gray-50 text-sm text-[#5C1A30] hover:bg-gray-100 transition-colors w-fit max-w-full"
                    >
                      <Paperclip className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate">{x.nom || "Voir le document"}</span>
                    </a>
                  ))}
                </div>
              </div>
            );
          })()}

          {etape.commentaire && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Commentaire</p>
              <p className="text-sm text-gray-800 bg-gray-50 rounded-xl px-3 py-2 whitespace-pre-wrap">
                {etape.commentaire}
              </p>
            </div>
          )}

          {etape.notifiedUsers?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Personnes notifiées</p>
              <div className="flex flex-wrap gap-2">
                {etape.notifiedUsers.map((u) => (
                  <span
                    key={u.id}
                    className="text-xs font-medium bg-[#9B1B5D]/10 text-[#9B1B5D] px-2.5 py-1 rounded-full"
                  >
                    {u.name} {u.lastName}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ClotureDetailsOverlay;
